/**
 * Feature icons for the v3 landing page, extracted 1:1 from the Landing Figma
 * (file qvKH4DGebnMCNK7SJ72iCm, nodes 2:3 / 6:1966) and normalized onto the
 * 24-unit lucide grid so they can be tinted with `currentColor` and sized via
 * `className`. These glyphs don't exist in the design system yet (see
 * `shared/src/ui/icons`) — they live page-local until the DS adopts them.
 */

import type { ReactNode } from 'react';

type LandingIconProps = {
  className?: string;
  /** Stroke override — defaults to each glyph's Figma-exact weight. */
  strokeWidth?: number;
};

function IconSvg({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}>
      {children}
    </svg>
  );
}

/** Flashka flashcard glyph (custom, node 5513:101067). */
export function FlashcardsIcon({ className, strokeWidth = 1.86 }: LandingIconProps) {
  return (
    <IconSvg className={className}>
      <rect
        x={4.04}
        y={4.04}
        width={15.92}
        height={15.92}
        rx={2.59}
        strokeWidth={strokeWidth}
        strokeLinecap="butt"
      />
      <path d="M4.04 12.08H19.96" strokeWidth={strokeWidth} strokeLinecap="butt" />
    </IconSvg>
  );
}

/** "network" glyph — Mind maps. */
export function MindMapsIcon({ className, strokeWidth = 2 }: LandingIconProps) {
  return (
    <IconSvg className={className}>
      <path
        strokeWidth={strokeWidth}
        d="M5 16v-3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3M12 12V8M17 16h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1M3 16h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1M10 2h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1"
      />
    </IconSvg>
  );
}

/** "file-audio" glyph — Transcriber. */
export function TranscriberIcon({ className, strokeWidth = 2 }: LandingIconProps) {
  return (
    <IconSvg className={className}>
      <path
        strokeWidth={strokeWidth}
        d="M17.5 22h.5a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v3M14 2v4a2 2 0 0 0 2 2h4M2 19a2 2 0 0 1 4 0v1a2 2 0 0 1-4 0v-4a6 6 0 0 1 12 0v4a2 2 0 0 1-4 0v-1a2 2 0 0 1 4 0"
      />
    </IconSvg>
  );
}

/** "notebook-pen" glyph — Summaries & notes. */
export function SummariesIcon({ className, strokeWidth = 2 }: LandingIconProps) {
  return (
    <IconSvg className={className}>
      <path
        strokeWidth={strokeWidth}
        d="M13.4 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7.4M2 6h4M2 10h4M2 14h4M2 18h4M21.378 5.626a2.121 2.121 0 1 0-3.004-3.004l-5.01 5.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z"
      />
    </IconSvg>
  );
}

/** "image-upscale" glyph — Image occlusion. */
export function ImageOcclusionIcon({ className, strokeWidth = 2 }: LandingIconProps) {
  return (
    <IconSvg className={className}>
      <path
        strokeWidth={strokeWidth}
        d="M21 8V3h-5M21 3l-5 5M17 21h2a2 2 0 0 0 2-2M21 12v3M3 7V5a2 2 0 0 1 2-2M5 21l4.144-4.144a1.21 1.21 0 0 1 1.712 0L13 19M9 3h3M4 11h8a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1"
      />
    </IconSvg>
  );
}

/** "calendar-clock" glyph — Exam planner. */
export function ExamPlannerIcon({ className, strokeWidth = 2 }: LandingIconProps) {
  return (
    <IconSvg className={className}>
      <path
        strokeWidth={strokeWidth}
        d="M16 14v2.2l1.6 1M16 2v4M21 7.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3.5M3 10h5M8 2v4M22 16a6 6 0 1 1-12 0 6 6 0 0 1 12 0"
      />
    </IconSvg>
  );
}

/** Custom chart glyph (node 10388:71709) — Spaced repetition. */
export function SpacedRepetitionIcon({ className, strokeWidth = 2 }: LandingIconProps) {
  return (
    <IconSvg className={className}>
      <path strokeWidth={strokeWidth} d="M3 3.4v16a2 2 0 0 0 2 2h16" />
      <path strokeWidth={strokeWidth} d="M7.8 18.4v-4.8M12.6 18.4v-7.2M17.4 18.4V7.6" />
      <path
        strokeWidth={1.2}
        d="M6 15.4l1.8-1.8 2.4 1.2 2.4-3.6 2.4 1.2 2.4-4.8 1.8-.6"
      />
    </IconSvg>
  );
}

/** Check-square glyph (node 5502:8263) — Quizzes & mock exams. */
export function QuizzesIcon({ className, strokeWidth = 2 }: LandingIconProps) {
  return (
    <IconSvg className={className}>
      <path
        strokeWidth={strokeWidth}
        d="M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
      />
    </IconSvg>
  );
}

/** "audio-lines" glyph — Oral exam. */
export function OralExamIcon({ className, strokeWidth = 2 }: LandingIconProps) {
  return (
    <IconSvg className={className}>
      <path strokeWidth={strokeWidth} d="M2 10v3M6 6v11M10 3v18M14 8v7M18 5v13M22 10v3" />
    </IconSvg>
  );
}

/** "clipboard-list" glyph — Case studies. */
export function CaseStudiesIcon({ className, strokeWidth = 2 }: LandingIconProps) {
  return (
    <IconSvg className={className}>
      <path
        strokeWidth={strokeWidth}
        d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2M12 11h4M12 16h4M8 11h.01M8 16h.01M9 2h6a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1"
      />
    </IconSvg>
  );
}

/**
 * "wand-sparkles" glyph from the hero CTA. Shaped like a lucide icon
 * (`color`/`size`/`strokeWidth` props) so the shared Button can inject color
 * and sizing via `leftIcon`.
 */
export function WandIcon({
  className,
  size = 24,
  color = 'currentColor',
  strokeWidth = 2,
}: LandingIconProps & { size?: number | string; color?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}>
      <path
        strokeWidth={strokeWidth}
        d="M15 4V2M15 16v-2M8 9h2M20 9h2M17.8 11.8L19 13M15 9h.01M17.8 6.2L19 5M3 21l9-9M12.2 6.2L11 5"
      />
    </svg>
  );
}
